import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import api from "@/lib/api";

const COLORS = ["#003B82", "#1B4B8A", "#2563EB", "#34C759", "#EA580C", "#DC2626", "#475569", "#94A3B8"];

function KPI({ label, value, hint, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-oc border border-oc-border p-7">
      <p className="text-[12px] uppercase tracking-[0.08em] font-semibold text-oc-textSecondary">{label}</p>
      <p className="font-mono text-oc-primary font-semibold mt-3 text-[28px] leading-none">{value}</p>
      {hint && <p className="text-oc-textSecondary text-[13px] mt-1">{hint}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => null);
    api.get("/admin/analyses?limit=10").then((r) => setRecent(r.data.items || [])).catch(() => null);
  }, []);

  const fmtEur = (cents) => `€ ${((cents || 0) / 100).toLocaleString("nl-BE", { minimumFractionDigits: 2 })}`;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display font-bold text-oc-primary text-[34px]">Dashboard</h1>
        <p className="text-oc-textSecondary mt-1">Overzicht van het platform.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testId="kpi-analyses-total" label="Analyses totaal" value={stats?.analyses?.total ?? "—"} hint={`${stats?.analyses?.month ?? 0} deze maand`} />
        <KPI testId="kpi-analyses-today" label="Analyses vandaag" value={stats?.analyses?.today ?? "—"} hint={`${stats?.analyses?.week ?? 0} deze week`} />
        <KPI testId="kpi-revenue" label="Omzet totaal" value={fmtEur(stats?.revenue_cents?.total)} hint={`${fmtEur(stats?.revenue_cents?.week)} deze week`} />
        <KPI testId="kpi-avg-score" label="Gem. score" value={stats?.avg_score ?? "—"} hint="Score 0-100 per offerte" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="bg-white rounded-oc border border-oc-border p-7 lg:col-span-2">
          <h3 className="font-display font-semibold text-oc-primary text-[18px]">Activiteit (14 dagen)</h3>
          <div className="h-72 mt-5">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats?.series_14d || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#475569" fontSize={12} />
                <YAxis stroke="#475569" fontSize={12} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12 }} />
                <Legend />
                <Line type="monotone" dataKey="analyses" stroke="#003B82" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="revenue" stroke="#34C759" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-oc border border-oc-border p-7">
          <h3 className="font-display font-semibold text-oc-primary text-[18px]">Per type</h3>
          <div className="h-72 mt-5">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats?.by_type || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={92}>
                  {(stats?.by_type || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-oc border border-oc-border p-7">
          <h3 className="font-display font-semibold text-oc-primary text-[18px]">Top regio's</h3>
          <div className="h-72 mt-5">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.by_region || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="#475569" fontSize={11} />
                <YAxis stroke="#475569" fontSize={12} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12 }} />
                <Bar dataKey="value" fill="#1B4B8A" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-oc border border-oc-border overflow-hidden">
          <div className="p-7 border-b border-oc-border">
            <h3 className="font-display font-semibold text-oc-primary text-[18px]">Recente analyses</h3>
          </div>
          <div className="divide-y divide-oc-border max-h-[296px] overflow-y-auto">
            {recent.map((r) => (
              <div key={r.id} className="px-7 py-3 flex items-center justify-between text-[13px]">
                <div>
                  <p className="font-display font-semibold text-oc-primary text-[14px]">{r.offerte_type}</p>
                  <p className="text-oc-textSecondary">{r.regio} · {r.customer?.email}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-oc-primary">{r.score ?? "—"}</p>
                  <p className="text-oc-textSecondary">{r.payment_status}</p>
                </div>
              </div>
            ))}
            {recent.length === 0 && <p className="px-7 py-10 text-center text-oc-textSecondary">Nog geen analyses</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
